/**
 * TAS Command — Sistema Digitale di Gestione Operazioni SAR
 *
 * Copyright (c) 2026 Matteo MESSINA, Alfonso CECERE
 * Tutti i diritti riservati.
 *
 * Sviluppato dagli autori a titolo personale, al di fuori
 * dell'orario di servizio e senza utilizzo di risorse o
 * strumentazione dell'amministrazione di appartenenza.
 *
 * Qualsiasi riproduzione, distribuzione o utilizzo non
 * autorizzato è vietato ai sensi della L. 633/1941.
 *
 * Autori:
 *   Matteo MESSINA  — VE, Comando VVF Benevento, Servizio TAS
 *   Alfonso CECERE  — VE, Comando VVF Benevento, Servizio TAS
 */

import { apiErr } from "@/lib/utils";
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster, toast } from "sonner";
import axios from "axios";

import { LoginPage, AutoRegisterPage, PrivacyPage, VerifyEmailPage, ResetPasswordPage, createApi, applyTheme, POA_ENABLED, AuthContext } from "./components/AppCore";
import { useTextGuard, soloSeCambiato, campiObbligatoriDaScheda, turnoDaConvertire, meteoInCache } from "./components/shared";
import AdminPanel from "./components/AdminPanel";
import RopPanel from "./components/RopPanel";
import { ReportPage } from "./components/DirRegPanel";
import ProfiloPage from "./components/ProfiloPage";
import SupportoPage from "./components/SupportoPage";
import StatoConnessione from "./components/StatoConnessione";
import ErrorBoundary from "./components/ErrorBoundary";
import { agganciaGestoriGlobali } from "./lib/segnalaErrori";
import confirmDialog from "./components/confirmDialog";
import { countPending, countFailed, syncPending, mergeKeepOffline, pendingDeletedIds, enqueueFromRequest } from "./lib/offlineQueue";
import CodaOffline from "./components/CodaOffline";
import { isOffline, subscribe as subscribeConnettivita, reportNetworkError, reportNetworkSuccess, probe as probeConnettivita } from "./lib/connectivity";
import { LayoutDashboard, Users, UsersRound, MapPin, ClipboardList, Search, Clock, Target, Shield, MessageSquare, LogOut, FileText, Settings, UserCircle, HelpCircle, ChevronLeft, Menu, BarChart2, Map, Radio, AlertTriangle, PhoneCall } from "lucide-react";

// Sidebar Component
const RUOLI_PANEL_LABEL = { rop: 'ROP', ror: 'ROR', ron: 'RON', dir_reg: 'DIR REG', con: 'CON', com_prov: 'COM PROV' };
const RUOLI_PANEL_SET = new Set(['rop','ror','ron','dir_reg','con','com_prov']);

// Timer discreto "tempo operazione": parte all'apertura (data/ora inizio) e, finché
// l'operazione è aperta, conta in tempo reale; alla chiusura si ferma e resta
// visibile mostrando il tempo trascorso fino al momento di chiusura
// (data_chiusura + ora_chiusura). Tutte le date sono wall-clock Europe/Rome: il
// diff resta corretto perché start ed end sono interpretati nello stesso fuso.
const OpTimer = ({ operazione }) => {
  const parseTs = (data, ora) => {
    if (!data) return null;
    const t = Date.parse(`${data}T${ora || '00:00:00'}`);
    return isNaN(t) ? null : t;
  };
  const startMs = parseTs(operazione.data_inizio, operazione.ora_inizio);
  const closed = !!operazione.chiusa;
  const endMs = closed ? parseTs(operazione.data_chiusura, operazione.ora_chiusura) : null;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (closed) return;            // operazione aperta → tick ogni secondo
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [closed]);
  if (startMs == null) return null;
  const end = closed ? (endMs != null ? endMs : startMs) : now;
  const elapsed = Math.max(0, end - startMs);
  const s = Math.floor(elapsed / 1000);
  const g = (n) => String(n).padStart(2, '0');
  const giorni = Math.floor(s / 86400);
  const hms = `${g(Math.floor((s % 86400) / 3600))}:${g(Math.floor((s % 3600) / 60))}:${g(s % 60)}`;
  const label = giorni > 0 ? `${giorni}g ${hms}` : hms;
  return (
    <div
      className={`text-[11px] font-mono mt-1 flex items-center gap-1 ${closed ? 'text-slate-500' : 'text-slate-400'}`}
      title={closed ? 'Operazione chiusa — durata totale' : 'Tempo trascorso dall’apertura'}
    >
      <Clock size={11} className={closed ? 'text-slate-500' : 'text-orange-500/70'} />
      <span>{label}</span>
      {closed && <span className="text-slate-600">(chiusa)</span>}
    </div>
  );
};

const Sidebar = ({ currentPage, onNavigate, operazioneAttiva, operatore, onLogout, pendingCount, gpsCount, sosCount = 0, onAdmin, offlinePendingCount, offlineFailedCount, onShowCoda, logoBase64, nomeSistema, isMobileOpen, isCollapsed, onToggleCollapsed, actingOperator, selfNome, onCambioTurno, isOffline, schedaDisperso, turnoAttivo, effemeridi }) => {
  const isAdmin = operatore?.ruolo === 'admin';
  const isPanelRole = RUOLI_PANEL_SET.has(operatore?.ruolo);
  const isViewer = ['dir_reg','con','com_prov'].includes(operatore?.ruolo);
  const navDispositiviRef = useRef(null);
  useTextGuard(navDispositiviRef, "Dispositivi");

  // Il pallino del turno dipende dall'ORA, non da un dato che arriva dal server:
  // senza questo battito resterebbe spento fino al primo evento che ricarica la
  // pagina, cioè proprio nel momento in cui deve accendersi (il tramonto).
  // Il tick sta QUI e non in MainApp di proposito: là rifarebbe il render di
  // tutta l'app — pagine comprese — una volta al minuto.
  const [, setMinuto] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setMinuto(m => m + 1), 60000);
    return () => clearInterval(id);
  }, []);

  // I due avvisi valgono solo dove si può fare qualcosa: operazione selezionata
  // e aperta, e ruolo che può scrivere. Un pallino che chiede un'azione a chi non
  // può compierla è rumore, e insegna a ignorare anche gli altri.
  const opModificabile = !!operazioneAttiva && !operazioneAttiva.chiusa && !isViewer;
  // `null` = scheda non ancora caricata → niente pallino (vedi MainApp).
  const schedaMancanti = (opModificabile && schedaDisperso)
    ? campiObbligatoriDaScheda(schedaDisperso)
    : [];
  const turnoConvertireIn = opModificabile
    ? turnoDaConvertire(turnoAttivo?.nome_turno, effemeridi, meteoInCache(operazioneAttiva.id))
    : "";

  return (
  <aside className={`sidebar${isMobileOpen ? ' is-open' : ''}${isCollapsed ? ' is-collapsed' : ''}`} data-testid="sidebar">
    <div className="sidebar-header">
      <div className="sidebar-logo">
        {logoBase64
          ? <img src={logoBase64} alt="Logo" className="w-10 h-10 object-contain rounded-lg" />
          : <div className="sidebar-logo-icon"><Shield size={20} /></div>
        }
        <span className="sidebar-collapsible text-lg font-bold text-white">{nomeSistema || "TAS Command"}</span>
      </div>
      <button className="sidebar-toggle-btn" onClick={onToggleCollapsed} title={isCollapsed ? "Espandi menu" : "Comprimi menu"}>
        <ChevronLeft size={16} />
      </button>
    </div>
    
    <nav className="sidebar-nav">
      <div className="nav-section">
        <div className="nav-section-title">Navigazione</div>
        <button className={`nav-item ${currentPage === 'dashboard' ? 'active' : ''}`} onClick={() => onNavigate('dashboard')} data-testid="nav-dashboard">
          <LayoutDashboard /><span>Dashboard</span>
        </button>
        {!isViewer && (
          <button className={`nav-item ${currentPage === 'soccorritori' ? 'active' : ''}`} onClick={() => onNavigate('soccorritori')} data-testid="nav-soccorritori">
            <Users /><span>Soccorritori</span>
            {pendingCount > 0 && <span className="ml-auto bg-yellow-500 text-black text-xs px-1.5 rounded-full">{pendingCount}</span>}
          </button>
        )}
        {!isViewer && (
          <button className={`nav-item ${currentPage === 'squadre' ? 'active' : ''}`} onClick={() => onNavigate('squadre')} data-testid="nav-squadre">
            <UsersRound /><span>Squadre</span>
          </button>
        )}
        {!isViewer && (
          <button className={`nav-item ${currentPage === 'missioni' ? 'active' : ''}`} onClick={() => onNavigate('missioni')} data-testid="nav-missioni">
            <MapPin /><span>Zone/Linee/Punti</span>
          </button>
        )}
        {!isViewer && (
          <button className={`nav-item ${currentPage === 'dispositivi' ? 'active' : ''}`} onClick={() => onNavigate('dispositivi')} data-testid="nav-dispositivi">
            <Radio /><span ref={navDispositiviRef}>Dispositivi</span>
            {/* Senza GPS censiti non si possono assegnare le squadre alle missioni:
                pallino giallo finche' non ne esiste almeno uno (come il badge dei
                soccorritori da validare). */}
            {operazioneAttiva && gpsCount === 0 && (
              <span
                className="ml-auto w-2 h-2 rounded-full bg-yellow-500 shrink-0"
                data-testid="nav-dispositivi-alert"
                title="Nessun dispositivo GPS inserito"
              />
            )}
          </button>
        )}
        {!isViewer && (
          <button className={`nav-item ${currentPage === 'mappa' ? 'active' : ''}`} onClick={() => onNavigate('mappa')} data-testid="nav-mappa">
            <Map /><span>Mappa</span>
            {/* SOS in corso: pallino ROSSO, come quello giallo dei dispositivi.
                L'avviso a schermo passa e l'operatore puo' essere su un'altra
                pagina; il pallino resta finche' l'SOS non e' preso in carico. */}
            {operazioneAttiva && sosCount > 0 && (
              <span
                className="ml-auto w-2.5 h-2.5 rounded-full bg-red-500 shrink-0 animate-pulse"
                data-testid="nav-mappa-sos"
                title={sosCount === 1 ? 'SOS in corso da un soccorritore' : `${sosCount} SOS in corso`}
              />
            )}
          </button>
        )}
        {POA_ENABLED && !isViewer && (
          <button className={`nav-item ${currentPage === 'poa' ? 'active' : ''}`} onClick={() => onNavigate('poa')} data-testid="nav-poa">
            <Target /><span>POA</span>
          </button>
        )}
        {!isViewer && (
          <button className={`nav-item ${currentPage === 'cronologico' ? 'active' : ''}`} onClick={() => onNavigate('cronologico')} data-testid="nav-cronologico">
            <MessageSquare /><span>Cronologico</span>
          </button>
        )}
        <button className={`nav-item ${currentPage === 'scheda-disperso' ? 'active' : ''}`} onClick={() => onNavigate('scheda-disperso')} data-testid="nav-scheda-disperso">
          <Search /><span>Scheda Disperso</span>
          {/* Campi obbligatori della Scheda 1 ancora vuoti: sono quelli senza cui
              la scheda non si salva. Stesso pallino dei dispositivi; l'elenco dei
              campi e' quello di shared.js, uno solo per pagina e pallino. */}
          {schedaMancanti.length > 0 && (
            <span
              className="ml-auto w-2 h-2 rounded-full bg-yellow-500 shrink-0"
              data-testid="nav-scheda-disperso-alert"
              title={`Campi obbligatori non ancora compilati: ${schedaMancanti.join(', ')}`}
            />
          )}
        </button>
        {/* Sala operativa del Comando: le schede raccolte al telefono prima che
            l'operazione esista. Unica voce in SCRITTURA per il ruolo COM PROV,
            che resta di sola consultazione su tutto il resto. */}
        {['com_prov','admin'].includes(operatore?.ruolo) && (
          <button className={`nav-item ${currentPage === 'schede-preliminari' ? 'active' : ''}`} onClick={() => onNavigate('schede-preliminari')} data-testid="nav-schede-preliminari">
            <PhoneCall /><span>Sala Operativa</span>
          </button>
        )}
        <button className={`nav-item ${currentPage === 'turno' ? 'active' : ''}`} onClick={() => onNavigate('turno')} data-testid="nav-turno">
          <ClipboardList /><span>Riepilogo Turno</span>
          {/* Il turno attivo non copre piu' questa fascia oraria: un turno di
              giorno che ha passato il tramonto va convertito in notturno, e
              viceversa all'alba. Resta acceso finche' non si apre il turno nuovo. */}
          {turnoConvertireIn && (
            <span
              className="ml-auto w-2 h-2 rounded-full bg-yellow-500 shrink-0"
              data-testid="nav-turno-alert"
              title={turnoConvertireIn === 'Notte'
                ? 'Il turno attivo è diurno ma è passato il tramonto: va aperto un turno di notte'
                : 'Il turno attivo è notturno ma è passata l’alba: va aperto un turno di giorno'}
            />
          )}
        </button>
        {isViewer && (
          <button className={`nav-item ${currentPage === 'report' ? 'active' : ''}`} onClick={() => onNavigate('report')} data-testid="nav-report">
            <BarChart2 /><span>Report</span>
          </button>
        )}
        {!isViewer && (
          <button className={`nav-item ${currentPage === 'supporto' ? 'active' : ''}`} onClick={() => onNavigate('supporto')} data-testid="nav-supporto">
            <HelpCircle /><span>Supporto</span>
          </button>
        )}
        {!isViewer && (
          <button className={`nav-item ${currentPage === 'logs' ? 'active' : ''}`} onClick={() => onNavigate('logs')} data-testid="nav-logs">
            <FileText /><span>Logs</span>
          </button>
        )}
        <button className={`nav-item ${currentPage === 'profilo' ? 'active' : ''}`} onClick={() => onNavigate('profilo')} data-testid="nav-profilo">
          <UserCircle /><span>Profilo</span>
        </button>
        {isPanelRole && !isViewer && (
          <button className="nav-item" onClick={onAdmin} data-testid="nav-panel" style={{ color: '#f97316', borderLeft: '2px solid #f97316' }}>
            <Settings /><span>Pannello {RUOLI_PANEL_LABEL[operatore?.ruolo]}</span>
          </button>
        )}
        {isAdmin && (
          <button className="nav-item" onClick={onAdmin} data-testid="nav-admin" style={{ color: '#f97316', borderLeft: '2px solid #f97316' }}>
            <Settings /><span>Admin</span>
          </button>
        )}
      </div>
    </nav>

    {operazioneAttiva && (
      <div className="p-4 border-t border-slate-800 sidebar-collapsible shrink-0">
        <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Operazione Attiva</div>
        <div className="text-sm font-medium text-orange-500 break-words leading-snug">{operazioneAttiva.nome}</div>
        <div className="text-xs text-slate-400 break-words leading-snug mt-0.5">
          {operazioneAttiva.comando_vvf}{operazioneAttiva.regione ? ` · ${operazioneAttiva.regione}` : ''}
        </div>
        <OpTimer operazione={operazioneAttiva} />
      </div>
    )}
    
    {/* Cliccabile: il solo numero non dice COSA non è ancora partito, e in campo —
        prima di spegnere il telefono o di lasciare la copertura — è proprio quello
        che serve sapere per decidere se si può chiudere. */}
    {offlinePendingCount > 0 && (
      <button
        type="button"
        onClick={onShowCoda}
        className="w-full text-left px-4 py-2 border-t border-amber-800/40 bg-amber-900/20 hover:bg-amber-900/30 sidebar-collapsible"
        title="Vedi cosa non è ancora stato sincronizzato"
      >
        <div className="flex items-center gap-2 text-amber-400 text-xs font-medium">
          <Clock size={12} />
          <span>{offlinePendingCount} {offlinePendingCount === 1 ? 'operazione in' : 'operazioni in'} attesa — vedi</span>
        </div>
      </button>
    )}

    {/* Rifiutate: rosso e CLICCABILE, a differenza del badge sopra. Queste non
        riproveranno da sole — se resta solo un numero, l'operatore non ha modo di
        sapere cosa non è partito né di rimediare. */}
    {offlineFailedCount > 0 && (
      <button
        type="button"
        onClick={onShowCoda}
        className="w-full text-left px-4 py-2 border-t border-red-800/40 bg-red-900/20 hover:bg-red-900/30 sidebar-collapsible"
        title="Operazioni rifiutate dal server: rivedi, rimanda o scarta"
      >
        <div className="flex items-center gap-2 text-red-400 text-xs font-medium">
          <AlertTriangle size={12} />
          <span>{offlineFailedCount} non riuscit{offlineFailedCount === 1 ? 'a' : 'e'} — rivedi</span>
        </div>
      </button>
    )}

    <div className="p-4 border-t border-slate-800 mt-auto">
      <div className="sidebar-collapsible">
        <div className="text-xs text-slate-500 mb-1">Ciao {operatore?.nome}! {operatore?.ruolo && <span className="text-slate-400">— {operatore.ruolo === 'admin' ? 'Admin' : RUOLI_PANEL_LABEL[operatore.ruolo] || 'Operatore'}</span>}</div>
        <div className="text-xs text-slate-400 truncate mb-2">{operatore?.email}</div>
        {actingOperator && actingOperator !== selfNome && (
          <div className="text-xs text-amber-400 truncate mb-2" title="Operatore che opera fisicamente alla postazione (cambio turno)">
            🔁 In turno: <span className="font-medium">{actingOperator}</span>
          </div>
        )}
      </div>
      {onCambioTurno && isOffline && (
        <button className="btn btn-secondary btn-sm w-full mb-2" onClick={onCambioTurno} data-testid="btn-cambio-turno" title="Passaggio di consegne offline: indica chi opera alla postazione" style={{ borderColor: '#6366f1', color: '#a5b4fc' }}>
          <UserCircle size={14} /> <span className="sidebar-collapsible">CAMBIO TURNO OFFLINE</span>
        </button>
      )}
      <button className="btn btn-secondary btn-sm w-full" onClick={onLogout} data-testid="btn-logout" title="Esci">
        <LogOut size={14} /> <span className="sidebar-collapsible">Esci</span>
      </button>
    </div>
  </aside>
  );
};

// ── Pagine lazy: un chunk per rotta, caricato solo all'apertura ──────────────
const DashboardPageM       = React.lazy(() => import("./components/DashboardPage"));
const SoccorritoriPageM    = React.lazy(() => import("./components/SoccorritoriPage"));
const SquadrePageM         = React.lazy(() => import("./components/SquadrePage"));
const MissioniPageM        = React.lazy(() => import("./components/MissioniPage"));
const DispositiviPageM     = React.lazy(() => import("./components/DispositiviPage"));
const MappaPageM           = React.lazy(() => import("./components/MappaPage"));
const POAPageM             = React.lazy(() => import("./components/POAPage"));
const CronologicoPageM     = React.lazy(() => import("./components/CronologicoPage"));
const SchedaDispersoPageM  = React.lazy(() => import("./components/SchedaDispersoPage"));
const SchedePreliminariPageM = React.lazy(() => import("./components/SchedePreliminariPage"));
const LogsPageM            = React.lazy(() => import("./components/LogsPage"));
// TurnoPage lazy: chunk separato, caricato solo all'apertura della pagina Turni
const TurnoPage            = React.lazy(() => import("./components/TurnoPage"));

// Main App Component
const MainApp = ({ operatore, token, onLogout, onTokenChange, logoBase64, nomeSistema }) => {
  const [showAdmin, setShowAdmin] = useState(false);
  // Primo accesso dopo la registrazione → parti dalla pagina profilo (una tantum)
  const [currentPage, setCurrentPage] = useState(
    () => (localStorage.getItem('tas_primo_accesso') ? 'profilo' : 'dashboard')
  );
  useEffect(() => { localStorage.removeItem('tas_primo_accesso'); }, []);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mapFocusMissione, setMapFocusMissione] = useState(null);
  // CTR (MBTiles) persistenti tra navigazioni: ref in MainApp, non in MappaPage
  const ctrDbs = useRef({});
  const ctrSeq = useRef(0);
  const [ctrCount, setCtrCount] = useState(0);
  const handleNavigate = useCallback((page) => {
    setCurrentPage(page);
    setMobileMenuOpen(false); // chiudi drawer mobile su navigazione
  }, []);
  const handleNavigateToMappa = useCallback((missione) => {
    setMapFocusMissione({ id: missione.id, nome: missione.nome, lat: missione.lat, lon: missione.lon, geometria_geojson: missione.geometria_geojson, centroide_lat: missione.centroide_lat, centroide_lon: missione.centroide_lon });
    handleNavigate('mappa');
  }, [handleNavigate]);
  const [operazioni, setOperazioni] = useState([]);
  const [operazioneAttiva, setOperazioneAttiva] = useState(null);
  const [soccorritori, setSoccorritori] = useState([]);
  const [pendingSoccorritori, setPendingSoccorritori] = useState([]);
  const [squadre, setSquadre] = useState([]);
  const [squadraPreselectIds, setSquadraPreselectIds] = useState([]);
  const [missioni, setMissioni] = useState([]);
  const [dispositivi, setDispositivi] = useState([]);
  const [stats, setStats] = useState({});
  // Servono ai due pallini di avviso in barra laterale (Scheda Disperso
  // incompleta, turno fuori dalla propria fascia oraria) e a nient'altro: qui
  // non si legge il contenuto, solo quanto basta a decidere se accendere il
  // puntino. Le pagine continuano a caricarsi i propri dati per conto loro.
  const [schedaDisperso, setSchedaDisperso] = useState(null);
  const [turnoAttivo, setTurnoAttivo] = useState(null);
  const [effemeridi, setEffemeridi] = useState(null);
  const [offlinePending, setOfflinePending] = useState(0);
  // Operazioni rifiutate dal server: non riproveranno da sole, aspettano una
  // decisione dell'operatore (rimandare o scartare). Vedi CodaOffline.
  const [offlineFailed, setOfflineFailed] = useState(0);
  const [showCoda, setShowCoda] = useState(false);

  // ── Cambio turno operatore (postazione condivisa, funziona offline) ──────────
  // Indica chi opera fisicamente alla postazione: NON cambia l'autenticazione
  // (resta il JWT di chi ha fatto login), ma viene annotato nell'audit.
  const selfNome = `${operatore?.cognome || ''} ${operatore?.nome || ''}`.trim();
  const [actingOperator, setActingOperator] = useState(() => localStorage.getItem('tas_acting_operator') || selfNome);
  const [showCambioTurno, setShowCambioTurno] = useState(false);
  const [cambioTurnoNome, setCambioTurnoNome] = useState("");
  // Il pulsante "Cambio turno offline" compare solo senza connessione.
  const [offline, setOffline] = useState(isOffline());
  useEffect(() => subscribeConnettivita((online) => setOffline(!online)), []);
  // Persiste l'operatore in turno e notifica il banner (StatoConnessione).
  useEffect(() => {
    if (actingOperator && actingOperator !== selfNome) localStorage.setItem('tas_acting_operator', actingOperator);
    else localStorage.removeItem('tas_acting_operator');
    window.dispatchEvent(new Event('acting-operator-changed'));
  }, [actingOperator, selfNome]);

  // `api` DEVE avere identita' stabile: quasi tutte le pagine lo mettono nelle
  // dipendenze degli useEffect di caricamento, quindi un oggetto nuovo a ogni
  // render significa rifare le fetch a ogni render — e sulla Scheda Disperso,
  // dove il form E' il dato caricato, significa cancellare quello che
  // l'operatore sta scrivendo. Finche' nulla ri-renderizzava MainApp il difetto
  // restava latente; col giro di sicurezza del realtime ogni 90s e' diventato
  // perdita di dati a video.
  const actingHeader = (actingOperator && actingOperator !== selfNome) ? actingOperator : "";
  const api = useMemo(() => createApi(token, actingHeader), [token, actingHeader]);

  const handleCambioTurno = async (nome) => {
    const n = (nome || '').trim();
    if (!n) { toast.error("Inserisci il nominativo dell'operatore in turno"); return; }
    setActingOperator(n);
    // Imposta subito la chiave così la chiamata seguente porta l'header acting.
    if (n !== selfNome) localStorage.setItem('tas_acting_operator', n); else localStorage.removeItem('tas_acting_operator');
    window.dispatchEvent(new Event('acting-operator-changed'));
    setShowCambioTurno(false);
    setCambioTurnoNome("");
    try {
      const res = await createApi(token).cambioOperatoreTurno({ operatore_in_turno: n, operazione_id: operazioneAttiva?.id || null });
      toast.success(res?.data?._offline ? "📵 Cambio turno registrato offline — sincronizzazione in sospeso" : `Operatore in turno: ${n}`);
    } catch (e) { toast.error(apiErr(e, "Errore registrazione cambio turno")); }
  };

  const loadOperazioni = useCallback(async () => {
    try {
      const { data } = await api.getOperazioni();
      setOperazioni(soloSeCambiato(data));
      if (data.length > 0 && !operazioneAttiva) {
        // Ripristina l'ultima operazione selezionata (se ancora accessibile),
        // altrimenti euristica: prima attiva, altrimenti la più recente.
        const savedId = localStorage.getItem("tas_operazione_attiva");
        const saved = savedId ? data.find(o => o.id === savedId) : null;
        setOperazioneAttiva(saved || data.find(o => o.attiva) || data[0]);
      }
    } catch { }
  }, [operazioneAttiva]);

  // Persiste l'operazione attiva così da ripristinarla dopo logout/refresh.
  useEffect(() => {
    if (operazioneAttiva?.id) {
      localStorage.setItem("tas_operazione_attiva", operazioneAttiva.id);
    }
  }, [operazioneAttiva]);

  // Aggiornamenti ottimistici offline — modificano lo stato locale immediatamente
  // senza aspettare loadData(), che userebbe la cache del SW (vecchia)
  const onOptimisticAddSoccorritore = useCallback((item) => {
    setSoccorritori(prev => [...prev, item]);
  }, []);

  const onOptimisticUpdateSoccorritore = useCallback((id, patch) => {
    setSoccorritori(prev => prev.map(s => s.id === id ? { ...s, ...patch, _offline: true } : s));
  }, []);

  const onOptimisticAddSquadra = useCallback((item) => {
    setSquadre(prev => [...prev, item]);
  }, []);

  const onOptimisticUpdateSquadra = useCallback((id, patch) => {
    setSquadre(prev => prev.map(s => s.id === id ? { ...s, ...patch, _offline: true } : s));
  }, []);

  const onOptimisticDeleteSquadra = useCallback((id) => {
    setSquadre(prev => prev.filter(s => s.id !== id));
  }, []);

  const onOptimisticAddMissione = useCallback((item) => {
    setMissioni(prev => [...prev, item]);
  }, []);

  const onOptimisticUpdateMissione = useCallback((id, patch) => {
    setMissioni(prev => prev.map(m => m.id === id ? { ...m, ...patch, _offline: true } : m));
  }, []);

  const onOptimisticDeleteSoccorritore = useCallback((id) => {
    setSoccorritori(prev => prev.filter(s => s.id !== id));
  }, []);

  const onOptimisticDeleteMissione = useCallback((id) => {
    setMissioni(prev => prev.filter(m => m.id !== id));
  }, []);

  const loadData = useCallback(async () => {
    if (!operazioneAttiva) return;
    try {
      const [soccRes, pendingRes, squadreRes, missioniRes, statsRes, dispositiviRes, schedaRes, turnoRes] = await Promise.all([
        api.getSoccorritori(operazioneAttiva.id),
        api.getPendingSoccorritori(operazioneAttiva.id),
        api.getSquadre(operazioneAttiva.id),
        api.getMissioni(operazioneAttiva.id),
        api.getStats(operazioneAttiva.id),
        api.getDispositivi(operazioneAttiva.id),
        // Le due sotto servono solo ai pallini di avviso, e hanno il `catch`
        // proprio: un errore qui deve spegnere un puntino, non far saltare il
        // giro di aggiornamento di soccorritori, squadre e missioni.
        api.getSchedaDisperso(operazioneAttiva.id).catch(() => ({ data: null })),
        api.getActiveTurno(operazioneAttiva.id).catch(() => ({ data: null })),
      ]);
      // Se ci sono operazioni offline in coda, fondi preservando gli aggiornamenti
      // ottimistici (_offline) così un refresh — anche da cache SW stale durante
      // connessioni ballerine — non cancella ciò che l'utente ha appena fatto.
      const pending = await countPending();
      if (pending > 0) {
        const deleted = await pendingDeletedIds();
        setSoccorritori(prev => mergeKeepOffline(soccRes.data, prev, deleted));
        setSquadre(prev => mergeKeepOffline(squadreRes.data, prev, deleted));
        setMissioni(prev => mergeKeepOffline(missioniRes.data, prev, deleted));
      } else {
        // `soloSeCambiato`: il giro di sicurezza gira ogni 90s anche quando non e'
        // successo niente. Rimpiazzando comunque gli array, React ri-renderizzava
        // tutte le liste e l'operatore vedeva la pagina rinfrescarsi sotto le mani.
        setSoccorritori(soloSeCambiato(soccRes.data));
        setSquadre(soloSeCambiato(squadreRes.data));
        setMissioni(soloSeCambiato(missioniRes.data));
      }
      setPendingSoccorritori(soloSeCambiato(pendingRes.data));
      setStats(soloSeCambiato(statsRes.data));
      setDispositivi(soloSeCambiato(dispositiviRes.data));
      setSchedaDisperso(soloSeCambiato(schedaRes.data));
      setTurnoAttivo(soloSeCambiato(turnoRes.data));
    } catch { }
  }, [operazioneAttiva]);

  useEffect(() => { loadOperazioni(); }, [loadOperazioni]);
  useEffect(() => { loadData(); }, [loadData]);

  // Cambiando operazione, i dati dei pallini della precedente non devono
  // sopravvivere nemmeno un istante: segnalerebbero sulla nuova operazione lo
  // stato di quella vecchia. `null` = "non lo so ancora", e in quel caso il
  // pallino resta spento — un avviso va acceso su un fatto, non su un'attesa.
  useEffect(() => {
    setSchedaDisperso(null);
    setTurnoAttivo(null);
  }, [operazioneAttiva?.id]);

  // Medie mensili di alba e tramonto: servono a sapere se il turno attivo copre
  // ancora questa fascia oraria. File statico e precachato, quindi risponde
  // anche ad app aperta a freddo senza campo.
  useEffect(() => {
    fetch('/effemeridi.json').then(r => r.json()).then(setEffemeridi).catch(() => {});
  }, []);

  // ── Realtime: stream SSE dell'operazione attiva. Quando un altro operatore
  //    modifica dati (missioni/comunicazioni/squadre/…) il server fa push e qui
  //    ricarichiamo (debounced) + notifichiamo le pagine via evento window. ──
  //
  //    RICONNESSIONE: EventSource riprova da solo SOLO se cade la rete. Se il
  //    server risponde con uno status != 200 (JWT scaduto dopo 8h, 502/504 del
  //    proxy durante un deploy) il browser chiude lo stream PER SEMPRE, in
  //    silenzio: l'operatore continua a lavorare su dati fermi. Qui riapriamo a
  //    mano con backoff, e un watchdog controlla che arrivi almeno un evento o un
  //    ping (il server ne manda uno ogni 20s) entro 60s.
  useEffect(() => {
    if (!operazioneAttiva || !token) return;
    const base = process.env.REACT_APP_BACKEND_URL;
    const url = `${base}/api/operazioni/${operazioneAttiva.id}/stream?token=${encodeURIComponent(token)}`;
    let es = null, t = null, retry = null, watchdog = null, tentativi = 0, chiuso = false;
    let ultimoSegnale = Date.now();
    const onPush = (kind) => {
      // Le posizioni soccorritori cambiano spesso (~ogni 25s per soccorritore): NON
      // fare il refetch totale dei dati operazione. Notifica solo la mappa, che
      // ricarica le sole posizioni e aggiorna i marker in place (no rebuild iframe).
      if (kind === 'posizioni' || kind === 'sos' || kind === 'sos-clear') {
        // SOS: avviso globale (l'operatore può essere su un'altra pagina) → poi gestisce sulla Mappa.
        // 'sos-clear' (annullo/presa in carico) NON rigenera l'allarme: solo refresh posizioni.
        if (kind === 'sos') toast.warning('🆘 SOS ricevuto da un soccorritore — apri la Mappa', { duration: 12000 });
        // Il pallino rosso sulla voce Mappa legge `sos_attivo` dall'elenco
        // soccorritori: senza un refetch resterebbe spento (o acceso dopo la
        // revoca). Qui si può fare — un SOS è un evento raro, a differenza di
        // 'posizioni' che arriva ogni ~25s per soccorritore.
        if (kind === 'sos' || kind === 'sos-clear') loadData();
        window.dispatchEvent(new CustomEvent('tas:realtime', { detail: { kind } }));
        return;
      }
      clearTimeout(t);
      t = setTimeout(() => {
        loadData();
        // L'anagrafica operazione (PCA, canali radio, TAS2, chiusura) sta nella
        // lista operazioni, che loadData non tocca: senza questo un operatore
        // non vedeva mai le modifiche fatte da un altro sull'operazione stessa.
        if (kind === 'operazione') loadOperazioni();
        window.dispatchEvent(new CustomEvent('tas:realtime', { detail: { kind } }));
      }, 400);
    };
    const connetti = () => {
      if (chiuso) return;
      try {
        es = new EventSource(url);
      } catch {
        return;  // ambiente senza EventSource: resta il polling di sicurezza
      }
      es.onopen = () => {
        ultimoSegnale = Date.now();
        // Riconnessione riuscita: nell'intervallo di buco possono essere successe
        // cose che non ci sono state notificate → riallinea subito.
        if (tentativi > 0) { tentativi = 0; loadData(); window.dispatchEvent(new CustomEvent('tas:realtime', { detail: { kind: 'reconnect' } })); }
      };
      es.onmessage = (e) => { ultimoSegnale = Date.now(); onPush(e.data); };
      // Heartbeat del server: evento NOMINATO, così i client vecchi (JS ancora in
      // cache) lo ignorano invece di scambiarlo per una modifica e rifare il
      // refetch ogni 20 secondi.
      es.addEventListener('ping', () => { ultimoSegnale = Date.now(); });
      es.onerror = () => {
        if (chiuso) return;
        // readyState CONNECTING = caduta di rete, ci pensa il browser.
        // CLOSED = errore HTTP: da qui in poi non riprova più, tocca a noi.
        if (es && es.readyState === 2) riconnetti();
      };
    };
    const riconnetti = () => {
      if (chiuso || retry) return;
      try { if (es) es.close(); } catch { /* già chiuso */ }
      es = null;
      tentativi += 1;
      const attesa = Math.min(30000, 2000 * Math.pow(2, Math.min(tentativi - 1, 4)));  // 2s→30s
      retry = setTimeout(() => { retry = null; connetti(); }, attesa);
    };
    connetti();
    // Watchdog: nessun evento né ping da oltre 60s = stream morto senza che il
    // browser ce lo abbia detto (proxy che tronca la connessione, sospensione).
    watchdog = setInterval(() => {
      // Offline non si insiste: la riapertura la fa il probe di connettività
      // quando il server torna raggiungibile (inutile svegliare la radio).
      if (chiuso || document.visibilityState !== 'visible' || isOffline()) return;
      if (Date.now() - ultimoSegnale > 60000) { ultimoSegnale = Date.now(); riconnetti(); }
    }, 20000);
    return () => {
      chiuso = true;
      clearTimeout(t); clearTimeout(retry); clearInterval(watchdog);
      if (es) es.close();
    };
  }, [operazioneAttiva, token, loadData, loadOperazioni]);

  // ── Rete di sicurezza sul realtime ──────────────────────────────────────────
  //    Lo stream puo' morire o perdere eventi senza che nessuno se ne accorga.
  //    Qui si riallinea comunque: al rientro sulla scheda/finestra (throttle 10s)
  //    e con un giro lento ogni 90s mentre la pagina e' visibile.
  //    Evento separato `tas:refresh`: le pagine con fetch proprio lo ascoltano,
  //    la Mappa NO — reagisce solo al push, altrimenti ricostruirebbe l'iframe
  //    ogni 90 secondi perdendo zoom e stato.
  useEffect(() => {
    if (!operazioneAttiva || !token) return;
    let ultimo = Date.now();
    const refresh = (minIntervallo) => {
      if (isOffline() || document.visibilityState !== 'visible') return;
      if (Date.now() - ultimo < minIntervallo) return;
      ultimo = Date.now();
      loadData();
      loadOperazioni();
      window.dispatchEvent(new CustomEvent('tas:refresh'));
    };
    const onVisible = () => refresh(10000);
    const interval = setInterval(() => refresh(90000), 90000);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [operazioneAttiva, token, loadData, loadOperazioni]);

  // ── Polling soccorritori in attesa di validazione ───────────────────────────
  // Quando un soccorritore si auto-iscrive tramite QR code il frontend non
  // riceve eventi in push. Polling leggero ogni 20s della sola lista pending
  // (no full reload), con toast quando arriva un nuovo nominativo.
  useEffect(() => {
    if (!operazioneAttiva) return;
    const POLL_INTERVAL = 20000;

    const pollPending = async () => {
      if (isOffline()) return;
      if (document.visibilityState !== 'visible') return;
      try {
        const { data } = await api.getPendingSoccorritori(operazioneAttiva.id);
        setPendingSoccorritori(prev => {
          // ── Confronto identità dati: se i pending sono identici, mantieni
          // lo STESSO riferimento all'array così React NON triggera re-render
          // e l'UI non lampeggia ogni 20s. JSON.stringify è OK qui perché la
          // lista pending è piccola (massimo qualche decina di record).
          if (JSON.stringify(prev) === JSON.stringify(data)) {
            return prev;
          }
          // I dati sono cambiati: notifica solo i NUOVI arrivati e solo se
          // NON è il primo caricamento (prev non vuoto, per evitare toast
          // di benvenuto sui pending già esistenti al boot dell'app).
          if (prev.length > 0) {
            const prevIds = new Set(prev.map(p => p.id));
            const newOnes = data.filter(d => !prevIds.has(d.id));
            newOnes.forEach(n => toast.info(`Nuovo soccorritore in attesa: ${n.cognome_nome || 'senza nome'}`));
          }
          return data;
        });
      } catch { /* errori silenziati: polling non deve disturbare */ }
    };

    const interval = setInterval(pollPending, POLL_INTERVAL);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') pollPending();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [operazioneAttiva, api]);

  // ── Offline sync — Fase 3: Background Sync + iOS fallback + conflict check ──

  // Guard anti-double-sync: un solo ciclo alla volta
  const syncInProgress = useRef(false);

  /**
   * Funzione centrale di sync.
   * Chiamata da tutti i trigger: online, Background Sync SW, visibilitychange, focus.
   */
  const runSync = useCallback(async () => {
    if (syncInProgress.current) return;
    if (isOffline() || !token) return;
    const count = await countPending();
    if (count === 0) return;

    syncInProgress.current = true;
    try {
      const { ok, fail, conflicts, rejected } = await syncPending(token);
      if (ok > 0) {
        toast.success(`✅ ${ok} operazion${ok === 1 ? 'e sincronizzata' : 'i sincronizzate'}`);
        loadData();
      }
      if (conflicts > 0) {
        toast.warning(`⚠️ ${conflicts} conflitt${conflicts === 1 ? 'o' : 'i'} — vince il dato del server, la tua versione è in "Non riuscite"`);
      }
      // Rifiutate: NON dire "riprova", perché da sole non riproveranno più. Sono in
      // failedOps e aspettano una decisione: rimandarle o scartarle.
      if (rejected > 0) {
        toast.error(`⚠️ ${rejected} operazion${rejected === 1 ? 'e rifiutata' : 'i rifiutate'} — rivedi in "Non riuscite"`);
      }
      // Ancora in coda per un errore transitorio: queste sì che riproveranno da sole.
      if (fail > 0) {
        toast.warning(`${fail} operazion${fail === 1 ? 'e' : 'i'} non ancora sincronizzat${fail === 1 ? 'a' : 'e'} — riprovo più tardi`);
      }
      setOfflineFailed(await countFailed());
      setOfflinePending(await countPending());
    } finally {
      syncInProgress.current = false;
    }
  }, [token, loadData]);

  // Badge sidebar: aggiorna al coda di ogni nuova operazione
  useEffect(() => {
    const refresh = () => {
      countPending().then(setOfflinePending);
      countFailed().then(setOfflineFailed);
    };
    refresh();
    window.addEventListener('offline-op-queued', refresh);
    return () => window.removeEventListener('offline-op-queued', refresh);
  }, []);

  // Avvio: sincronizza subito se ci sono operazioni pendenti
  useEffect(() => {
    countPending().then((count) => {
      setOfflinePending(count);
      if (count > 0) runSync();
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Trigger 1 — Ritorno online (probe attivo di raggiungibilità, non solo evento
  //   browser 'online': copre il caso "rete su ma server prima irraggiungibile").
  useEffect(() => {
    const unsub = subscribeConnettivita((online) => { if (online) runSync(); });
    return unsub;
  }, [runSync]);

  // Trigger 2 — Background Sync API (Chrome/Android)
  //   Il SW riceve 'sync' e invia SYNC_NEEDED al main thread
  useEffect(() => {
    const handler = (event) => {
      if (event.data?.type === 'SYNC_NEEDED') runSync();
    };
    navigator.serviceWorker?.addEventListener('message', handler);
    return () => navigator.serviceWorker?.removeEventListener('message', handler);
  }, [runSync]);

  // Trigger 3 — Fallback iOS/Safari
  //   SyncManager non è disponibile su iOS: usiamo visibilitychange e focus
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') runSync(); };
    const onFocus   = () => runSync();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, [runSync]);

  // Trigger 4 — Resync affidabile dopo un reload/login con coda pendente.
  //   Dopo un refresh la rete può essere già attiva (nessun nuovo evento 'online')
  //   e il token può arrivare dopo il primo tick: questo effetto lancia la sync
  //   appena token+rete+coda sono pronti e ritenta ogni 15s finché la coda non si
  //   svuota, così il lavoro offline non resta bloccato/invisibile dopo il reload.
  useEffect(() => {
    if (!token || offlinePending === 0) return;
    if (!isOffline()) runSync();
    const iv = setInterval(() => { if (!isOffline()) runSync(); }, 15000);
    return () => clearInterval(iv);
  }, [token, offlinePending, runSync]);

  // Guard anti-refresh: se ci sono operazioni offline non ancora sincronizzate,
  //   avvisa prima di ricaricare/chiudere la pagina (il testo è generico, imposto
  //   dal browser). Evita la sparizione a video del lavoro per un refresh accidentale.
  useEffect(() => {
    const handler = (e) => {
      if (offlinePending > 0) {
        e.preventDefault();
        e.returnValue = ''; // richiesto da Chrome/Edge per mostrare il prompt
        return '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [offlinePending]);

  const handleCreateOperazione = async (data) => {
    try {
      const { data: newOp } = await api.createOperazione(data);
      setOperazioneAttiva(newOp);
      toast.success(`Operazione ${newOp.nome} creata`);
      // Aggiorna la lista in background — non blocca il ritorno del codice accesso
      loadOperazioni().catch(() => {});
      return newOp; // include codice_accesso per il modal
    } catch (err) {
      toast.error(apiErr(err, "Errore creazione operazione"));
      return null;
    }
  };

  const handleAccediConCodice = async (codice) => {
    const { data: op } = await api.accediConCodice(codice);
    setOperazioni(prev => prev.find(o => o.id === op.id) ? prev : [op, ...prev]);
    setOperazioneAttiva(op);
    return op;
  };

  const handleUpdateOperazione = async (id, data) => {
    try {
      const { data: updated } = await api.patchOperazione(id, data);
      setOperazioneAttiva(updated);
      setOperazioni(prev => prev.map(o => o.id === id ? updated : o));
      toast.success("Operazione aggiornata");
      return updated;
    } catch (err) {
      toast.error(apiErr(err, "Errore aggiornamento operazione"));
      return null;
    }
  };

  const renderPage = () => {
    const props = { operazione: operazioneAttiva, soccorritori, pendingSoccorritori, squadre, missioni, dispositivi, stats, api, onRefresh: loadData, onNavigate: setCurrentPage, onOptimisticAddSoccorritore, onOptimisticUpdateSoccorritore, onOptimisticDeleteSoccorritore, onOptimisticAddSquadra, onOptimisticUpdateSquadra, onOptimisticDeleteSquadra, onOptimisticAddMissione, onOptimisticUpdateMissione, onOptimisticDeleteMissione, logoBase64, nomeSistema, operatore };
    switch (currentPage) {
      case 'dashboard': return <DashboardPageM {...props} operatore={operatore} operazioni={operazioni} onCreateOperazione={handleCreateOperazione} onSelectOperazione={setOperazioneAttiva} onUpdateOperazione={handleUpdateOperazione} onAccediConCodice={handleAccediConCodice} onChiudiOperazione={(chiusa) => { setOperazioneAttiva(chiusa); setOperazioni(prev => prev.map(o => o.id === chiusa.id ? chiusa : o)); }} />;
      case 'soccorritori': return <SoccorritoriPageM {...props} onCreaSquadra={(ids) => { setSquadraPreselectIds(ids); handleNavigate('squadre'); }} />;
      case 'squadre': return <SquadrePageM {...props} preselectIds={squadraPreselectIds} onPreselectClear={() => setSquadraPreselectIds([])} />;
      case 'missioni': return <MissioniPageM {...props} onNavigateToMappa={handleNavigateToMappa} />;
      case 'dispositivi': return <DispositiviPageM {...props} />;
      case 'mappa':
        // Mappa non disponibile ai ruoli di sola consultazione (CON/DIR_REG/COM_PROV)
        if (['dir_reg','con','com_prov'].includes(operatore?.ruolo)) { setTimeout(() => setCurrentPage('dashboard'), 0); return null; }
        return <MappaPageM {...props} focusMissione={mapFocusMissione} onFocusClear={() => setMapFocusMissione(null)} ctrDbs={ctrDbs} ctrSeq={ctrSeq} ctrCount={ctrCount} setCtrCount={setCtrCount} />;
      case 'poa':
        // Se POA disabilitato e utente arriva sulla rotta in qualche modo
        // (es. URL salvato, bookmark), reindirizza alla Dashboard
        if (!POA_ENABLED) { setTimeout(() => setCurrentPage('dashboard'), 0); return null; }
        return <POAPageM {...props} />;
      case 'turno': return <TurnoPage {...props} operatore={operatore} />;
      case 'cronologico': return <CronologicoPageM {...props} onNavigateToMappa={handleNavigateToMappa} />;
      case 'logs': return <LogsPageM {...props} />;
      case 'scheda-disperso': return <SchedaDispersoPageM {...props} logoBase64={logoBase64} />;
      case 'schede-preliminari':
        // Schede raccolte dalla sala operativa: riservata al Comando Provinciale
        // (l'admin passa di qui per assistenza). Non dipende dall'operazione
        // attiva — queste schede nascono prima che l'operazione esista.
        if (!['com_prov', 'admin'].includes(operatore?.ruolo)) { setTimeout(() => setCurrentPage('dashboard'), 0); return null; }
        return <SchedePreliminariPageM {...props} />;
      // onNavigate serve al pulsante FAQ della pagina per rimandare a Supporto
      case 'profilo': return <ProfiloPage api={api} operatore={operatore} onTokenChange={onTokenChange} onNavigate={setCurrentPage} />;
      case 'supporto': return <SupportoPage api={api} operatore={operatore} operazione={operazioneAttiva} />;
      case 'report':
        // Report nazionale/regionale per ruoli CON / DIR_REG (ex pannello dedicato)
        if (!['dir_reg','con','com_prov'].includes(operatore?.ruolo)) { setTimeout(() => setCurrentPage('dashboard'), 0); return null; }
        return <ReportPage token={token} />;
      default: return null;
    }
  };

  if (showAdmin) {
    const ruolo = operatore?.ruolo;
    if (ruolo === 'admin') {
      return <AdminPanel operatore={operatore} token={token} onBack={() => setShowAdmin(false)} operazioneAttiva={operazioneAttiva} logoBase64={logoBase64} />;
    }
    if (ruolo === 'rop' || ruolo === 'ror' || ruolo === 'ron') {
      return <RopPanel operatore={operatore} token={token} onBack={() => setShowAdmin(false)} logoBase64={logoBase64} />;
    }
    return <AdminPanel operatore={operatore} token={token} onBack={() => setShowAdmin(false)} operazioneAttiva={operazioneAttiva} logoBase64={logoBase64} />;
  }

  return (
    <div className="app-container">
      <button
        className="mobile-menu-toggle"
        aria-label="Apri menu"
        onClick={() => setMobileMenuOpen(true)}
      >
        <Menu size={20} />
      </button>
      <div
        className={`mobile-menu-backdrop${mobileMenuOpen ? ' is-open' : ''}`}
        onClick={() => setMobileMenuOpen(false)}
      />
      <Sidebar currentPage={currentPage} onNavigate={handleNavigate} operazioneAttiva={operazioneAttiva} operatore={operatore} onLogout={onLogout} pendingCount={pendingSoccorritori.length} gpsCount={dispositivi.filter(d => d.tipo === 'GPS').length} sosCount={soccorritori.filter(s => s.sos_attivo).length} onAdmin={() => { setShowAdmin(true); setMobileMenuOpen(false); }} offlinePendingCount={offlinePending} offlineFailedCount={offlineFailed} onShowCoda={() => { setShowCoda(true); setMobileMenuOpen(false); }} logoBase64={logoBase64} nomeSistema={nomeSistema} isMobileOpen={mobileMenuOpen} isCollapsed={sidebarCollapsed} onToggleCollapsed={() => setSidebarCollapsed(c => !c)} actingOperator={actingOperator} selfNome={selfNome} isOffline={offline} onCambioTurno={() => { setCambioTurnoNome(""); setShowCambioTurno(true); setMobileMenuOpen(false); }} schedaDisperso={schedaDisperso} turnoAttivo={turnoAttivo} effemeridi={effemeridi} />

      {showCambioTurno && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4" onClick={() => setShowCambioTurno(false)}>
          <div className="tactical-card w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-white font-bold mb-1">Cambio turno offline</h3>
            <p className="text-xs text-slate-400 mb-4">
              Passaggio di consegne sulla stessa postazione. La sessione di accesso resta
              attiva ({selfNome || operatore?.email}); le azioni successive vengono
              <strong> attribuite nell'audit</strong> all'operatore indicato e l'operazione
              è registrata nel log.
            </p>
            <label className="form-label">Operatore che subentra</label>
            <input
              className="form-input mb-3" autoFocus
              placeholder="Cognome Nome"
              value={cambioTurnoNome}
              onChange={e => setCambioTurnoNome(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleCambioTurno(cambioTurnoNome); }}
            />
            <div className="flex items-center justify-between gap-2">
              {actingOperator && actingOperator !== selfNome ? (
                <button className="btn btn-secondary btn-sm" onClick={() => handleCambioTurno(selfNome)} title="Torna a operare come te stesso">
                  Riprendi tu ({selfNome})
                </button>
              ) : <span />}
              <div className="flex gap-2">
                <button className="btn btn-secondary" onClick={() => setShowCambioTurno(false)}>Annulla</button>
                <button className="btn btn-primary" onClick={() => handleCambioTurno(cambioTurnoNome)}>Conferma</button>
              </div>
            </div>
          </div>
        </div>
      )}
      <CodaOffline
        isOpen={showCoda}
        onClose={() => setShowCoda(false)}
        onChange={() => { countFailed().then(setOfflineFailed); countPending().then(setOfflinePending); }}
        onSync={runSync}
        isOffline={offline}
      />
      <main className={`main-content${sidebarCollapsed ? ' sidebar-collapsed' : ''}`}>
        <ErrorBoundary key={currentPage}>
          <React.Suspense fallback={<div className="page"><div style={{ padding: '3rem', textAlign: 'center', color: '#94a3b8' }}>Caricamento…</div></div>}>
            {renderPage()}
          </React.Suspense>
        </ErrorBoundary>
        <footer style={{ textAlign: 'center', fontSize: '9px', color: '#1e293b', padding: '4px 0 8px' }}>
          Tutti i diritti riservati | © 2026 TAS Command
        </footer>
      </main>
    </div>
  );
};

// Root App
// Errori fuori dal ciclo di rendering (timer, callback, promesse non gestite) e
// riserva offline da svuotare. Fuori dal componente di proposito: deve valere
// anche per quello che accade prima del primo mount.
agganciaGestoriGlobali();

function App() {
  const [operatore, setOperatore] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logoBase64, setLogoBase64] = useState(null);
  const [nomeSistema, setNomeSistema] = useState("TAS Command");

  // Carica e applica il tema grafico al primo avvio (no auth richiesta)
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/impostazioni`)
      .then(({ data }) => {
        applyTheme(data);
        if (data.logo_base64) setLogoBase64(data.logo_base64);
        if (data.nome_sistema) setNomeSistema(data.nome_sistema);
      })
      .catch(() => {}); // Fallback silenzioso ai valori CSS predefiniti
  }, []);

  // Listener per aggiornamenti tema live (emesso da AdminPanel dopo il salvataggio)
  useEffect(() => {
    const handleThemeUpdate = (e) => {
      const d = e.detail || {};
      if (d.logo_base64 !== undefined) setLogoBase64(d.logo_base64 || null);
      if (d.nome_sistema !== undefined) setNomeSistema(d.nome_sistema || "TAS Command");
    };
    window.addEventListener('tas-theme-update', handleThemeUpdate);
    return () => window.removeEventListener('tas-theme-update', handleThemeUpdate);
  }, []);

  useEffect(() => {
    const savedToken = localStorage.getItem("tas_token");
    const savedOp = localStorage.getItem("tas_operatore");
    if (savedToken && savedOp) {
      // Validate token on startup — if invalid, force logout immediately
      // Use /me response to get fresh role/data (avoids stale localStorage)
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, { headers: { Authorization: `Bearer ${savedToken}` } })
        .then((res) => {
          const freshOp = { ...JSON.parse(savedOp), ...res.data };
          localStorage.setItem("tas_operatore", JSON.stringify(freshOp));
          setToken(savedToken);
          setOperatore(freshOp);
        })
        .catch((err) => {
          if (isOffline() || !err.response) {
            // Errore di rete (offline) — usa le credenziali in cache
            setToken(savedToken);
            setOperatore(JSON.parse(savedOp));
          } else {
            // Token non valido (401/403) — forza logout
            localStorage.removeItem("tas_token");
            localStorage.removeItem("tas_operatore");
          }
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  // Global axios 401 interceptor: token expired/invalid → forced logout
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (res) => {
        // Il Service Worker marca con X-TAS-Cache: hit le risposte servite dalla
        // cache. Una 200 del genere non ha mai visto la rete: se ci credevamo
        // online significa che la richiesta e' andata in timeout, quindi si
        // verifica con un probe (che bypassa il SW) invece di restare convinti
        // di essere raggiungibili mostrando dati vecchi senza dirlo.
        if (res?.headers?.['x-tas-cache'] === 'hit') probeConnettivita();
        else reportNetworkSuccess();
        return res;
      },
      (err) => {
        if (err?.response?.status === 401) {
          localStorage.removeItem("tas_token");
          localStorage.removeItem("tas_operatore");
          setOperatore(null);
          setToken(null);
          toast.error("Sessione scaduta. Effettua nuovamente il login.");
        } else if (!err?.response) {
          // Nessuna risposta = errore di rete: verifica subito la raggiungibilità
          // così il banner offline e l'accodamento scattano senza attendere.
          reportNetworkError();
          // ...e SALVA la scrittura che stava morendo. L'accodamento nei metodi api
          // scatta solo se l'app sapeva GIÀ di essere offline, ma la connettività si
          // verifica con un probe ogni 20s: nella finestra fra caduta del campo e
          // rilevamento, la richiesta partiva, moriva e l'azione andava persa con un
          // semplice toast d'errore. È il caso classico di chi chiude una battuta al
          // limite della copertura. Qui la richiesta viene rimessa in coda.
          return enqueueFromRequest(err.config)
            .then((accodata) => {
              if (accodata) {
                err.queuedOffline = true;   // apiErr lo traduce in un messaggio corretto
                countPending().then(setOfflinePending);
              }
              return Promise.reject(err);
            })
            .catch(() => Promise.reject(err));
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  const handleLogin = (op, tk) => { setOperatore(op); setToken(tk); };
  const handleLogout = async () => {
    if (isOffline()) {
      if (!(await confirmDialog({ title: "Esci offline", danger: true, confirmLabel: "Esci", message: "Sei offline.\nSe esci non potrai accedere finché non torni online.\nVuoi continuare?" }))) return;
    }
    localStorage.removeItem("tas_token");
    localStorage.removeItem("tas_operatore");
    localStorage.removeItem("tas_acting_operator"); // azzera l'operatore in turno
    window.dispatchEvent(new Event('acting-operator-changed')); // nascondi il banner cambio turno
    // tas_operazione_attiva: NON rimossa, così l'operazione resta ripristinabile
    // dopo logout→login (lo scope è comunque protetto dal filtro accessi backend).
    setOperatore(null);
    setToken(null);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-950"><div className="text-white">Caricamento...</div></div>;

  return (
    <AuthContext.Provider value={{ operatore, token }}>
      <ErrorBoundary>
      <StatoConnessione />
      <Toaster position="top-right" toastOptions={{ style: { background: '#0f172a', border: '1px solid #1e293b', color: '#f8fafc' } }} />
      <BrowserRouter>
        <Routes>
          <Route path="/register/:token" element={<AutoRegisterPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/*" element={operatore ? <MainApp operatore={operatore} token={token} onLogout={handleLogout} onTokenChange={setToken} logoBase64={logoBase64} nomeSistema={nomeSistema} /> : <LoginPage onLogin={handleLogin} />} />
        </Routes>
      </BrowserRouter>
      </ErrorBoundary>
    </AuthContext.Provider>
  );
}

export default App;
